import { useEffect, useRef, useState } from "react";
import { DataSource, DataRow, DataId, FilterValue, Filterable, Paged } from "../../general";

/**
 * The row state.
 */
export interface RowState {

  /**
   * The consolidated set of rows.
   */
  readonly rows: ReadonlyArray<DataRow<object>>;

  /**
   * A table indicating whether a specified data ID exists in the set of rows.
   */
  readonly indices: Readonly<Record<DataId, boolean>>;
}

/**
 * Consolidates rows incoming from a data source.
 * @param dataSource The data source to consolidate rows from.
 * @returns The row state.
 */
export function useConsolidatedRows<TRow extends object>(dataSource: DataSource<TRow> & Paged): RowState {
  const [rowState, setRowState] = useState<RowState>({ rows: [], indices: {} });
  const previousFilterRef = useRef<FilterValue>(null);

  useEffect(() => {
    setRowState((current) => {
      let { rows: currentRows, indices: currentIndices } = current;

      let filter: FilterValue | null = null;
      const filterableDatasource: Filterable | null = ((dataSource as unknown as Filterable)["getFilter"]) ? dataSource as unknown as Filterable : null;

      let requiresUpdate = false;

      if (filterableDatasource) {
        filter = filterableDatasource.getFilter();

        if (previousFilterRef.current !== filter) {
          currentRows = [];
          currentIndices = {};
          previousFilterRef.current = filter;
          requiresUpdate = true;
        }
      }

      const incomingRows: Array<DataRow<object>> = [];
      const incomingIndices: Record<DataId, boolean> = {};

      for (const row of dataSource.rows) {
        if (currentIndices[row.id])
          continue;

        incomingRows.push(row);
        incomingIndices[row.id] = true;
      }

      if (incomingRows.length > 0 || requiresUpdate) {
        return {
          rows: [...currentRows, ...incomingRows],
          indices: { ...currentIndices, ...incomingIndices },
        };
      }

      return current;
    });
  }, [dataSource.rows]);

  useEffect(() => {
    return () => {
      dataSource.first();
    };
  }, []);

  return rowState;
}
